import loader from 'scriptjs';
import Dependencies from './dependencies';


export default class DependencyManager {

	/**
	 * @param {Array<String|Array<String>>} dependencies
	 * @return {String}
	 */
	static loadDependencies(dependencies) {
		dependencies.flat().forEach(d => DependencyManager.loadStyle(Dependencies.styles[d]));

		const priority =  dependencies.filter(d => Array.isArray(d));
		const priorityId = DependencyManager.uniqueId();

		const standard = dependencies.filter(d => !Array.isArray(d));
		const standardId = DependencyManager.uniqueId();

		if (priority.length) {
			loader(priority, priorityId);
			loader.ready(priorityId, () => loader(standard, standardId));
		} else {
			loader(standard, standardId);
		}
		return standardId;
	}

	/**
	 * Loads dependencies on demand and resolves once they are all available. Use this for
	 * libraries that are only needed after a user interaction, so a module is not held back
	 * waiting on them.
	 * @param {Array<String|Array<String>>} dependencies
	 * @return {Promise<void>}
	 */
	static whenLoaded(dependencies) {
		const scripts = new Promise(resolve => loader.ready(DependencyManager.loadDependencies(dependencies), resolve));
		const styles = dependencies.flat().map(d => DependencyManager.#styles.get(Dependencies.styles[d]));
		return Promise.all([scripts, ...styles]).then(() => {});
	}

	/** @type {Map<String, Promise<void>>} */
	static #styles = new Map();

	/**
	 * Loads a stylesheet once. It is inserted before main.css, so the theme's own overrides of the
	 * plugin's rules keep winning exactly as they did when the plugin CSS was bundled.
	 * @param {String|undefined} href
	 * @return {Promise<void>}
	 */
	static loadStyle(href) {
		if (!href) {
			return Promise.resolve();
		}
		if (!DependencyManager.#styles.has(href)) {
			DependencyManager.#styles.set(href, new Promise(resolve => {
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = href;
				// A failed stylesheet should not stop the feature working
				link.onload = link.onerror = () => resolve();
				const main = document.querySelector('link[rel="stylesheet"][href*="dist/main.css"]');
				main ? main.before(link) : document.head.append(link);
			}));
		}
		return DependencyManager.#styles.get(href);
	}

	/**
	 * Loads the module's own dependencies, then creates an instance for the holder
	 * @param {AutoLoadModule} module
	 * @param {HTMLElement} holderElement
	 */
	static initModule(module, holderElement) {
		const dependencies = module.getDependencies?.() ?? [];
		if (dependencies.length) {
			loader.ready(DependencyManager.loadDependencies(dependencies), () => new module(holderElement));
		} else {
			new module(holderElement);
		}
	}

	static uniqueId() {
		return "id-" + Math.random().toString(16).slice(2);
	}

	static loadScript(script) {
		loader(script);
	}


}




